// redux/slices/broadcastTemplatesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

/* ========================= THUNKS ========================= */

export const submitBroadcastTemplate = createAsyncThunk(
  "broadcastTemplates/submitBroadcastTemplate",
  async (
    { uid, title, messageText, language },
    { rejectWithValue }
  ) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!title?.trim()) throw new Error("Название шаблона не передано");
      if (!messageText?.trim()) throw new Error("Текст шаблона не передан");
      if (!language) throw new Error("Язык шаблона не передан");

      const response = await apiRequest(
        "POST",
        "/broadcastTemplates/submit",
        {
          uid,
          title: title.trim(),
          messageText: messageText.trim(),
          language,
        }
      );

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        getError(error, "submitBroadcastTemplate failed")
      );
    }
  }
);

export const fetchBroadcastTemplates = createAsyncThunk(
  "broadcastTemplates/fetchBroadcastTemplates",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/broadcastTemplates/my/${uid}`
      );

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        getError(error, "fetchBroadcastTemplates failed")
      );
    }
  }
);

export const sendBroadcast = createAsyncThunk(
  "broadcastTemplates/sendBroadcast",
  async (
    { uid, templateId, recipientCount },
    { rejectWithValue }
  ) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!templateId) throw new Error("ID шаблона не передан");
      if (!recipientCount || recipientCount <= 0) {
        throw new Error("Количество получателей не передано");
      }

      const response = await apiRequest(
        "POST",
        "/broadcastTemplates/send",
        {
          uid,
          templateId,
          recipientCount,
        }
      );

      return response?.data || response;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        getError(error, "sendBroadcast failed")
      );
    }
  }
);

// Завершение партии рассылки: сервер считает доставленные по sent_sms
// и возвращает баланс за недоставленные
export const completeBroadcast = createAsyncThunk(
  "broadcastTemplates/completeBroadcast",
  async ({ uid, batchId }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!batchId) throw new Error("batchId не передан");

      const response = await apiRequest(
        "POST",
        "/broadcastTemplates/send/complete",
        { uid, batchId }
      );

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        getError(error, "completeBroadcast failed")
      );
    }
  }
);

export const deleteBroadcastTemplate = createAsyncThunk(
  "broadcastTemplates/deleteBroadcastTemplate",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID шаблона не передан");

      const response = await apiRequest(
        "DELETE",
        `/broadcastTemplates/${id}`
      );

      if (response?.status && response.status !== 200) {
        throw new Error(`Ошибка удаления. Код: ${response.status}`);
      }

      return id;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        try {
          await clearJwtToken();
        } catch {}
      }

      return rejectWithValue(
        getError(error, "deleteBroadcastTemplate failed")
      );
    }
  }
);

/* ========================= STATE ========================= */

const initialState = {
  templates: [],
  status: "idle",
  submitStatus: "idle",
  sendStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const broadcastTemplatesSlice = createSlice({
  name: "broadcastTemplates",
  initialState,
  reducers: {
    resetSubmitStatus: (state) => {
      state.submitStatus = "idle";
      state.error = null;
    },

    resetSendStatus: (state) => {
      state.sendStatus = "idle";
      state.error = null;
    },

    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },

    clearBroadcastTemplatesError: (state) => {
      state.error = null;
    },

    clearBroadcastTemplates: (state) => {
      state.templates = [];
      state.status = "idle";
      state.submitStatus = "idle";
      state.sendStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBroadcastTemplates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchBroadcastTemplates.fulfilled, (state, action) => {
        state.status = "succeeded";

        const templates = Array.isArray(action.payload)
          ? action.payload
          : Array.isArray(action.payload?.templates)
          ? action.payload.templates
          : [];

        state.templates = templates;
      })
      .addCase(fetchBroadcastTemplates.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchBroadcastTemplates failed";
      })

      .addCase(submitBroadcastTemplate.pending, (state) => {
        state.submitStatus = "loading";
        state.error = null;
      })
      .addCase(submitBroadcastTemplate.fulfilled, (state, action) => {
        state.submitStatus = "succeeded";

        const template =
          action.payload?.template ||
          action.payload;

        if (!template) return;

        const exists = state.templates.some(
          (item) => String(item?.id) === String(template?.id)
        );

        if (!exists) {
          state.templates.unshift(template);
        }
      })
      .addCase(submitBroadcastTemplate.rejected, (state, action) => {
        state.submitStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "submitBroadcastTemplate failed";
      })

      .addCase(sendBroadcast.pending, (state) => {
        state.sendStatus = "loading";
        state.error = null;
      })
      .addCase(sendBroadcast.fulfilled, (state, action) => {
        state.sendStatus = "succeeded";

        const template =
          action.payload?.template ||
          action.payload?.data?.template;

        if (!template?.id) return;

        const index = state.templates.findIndex(
          (item) => String(item?.id) === String(template.id)
        );

        if (index !== -1) {
          state.templates[index] = {
            ...state.templates[index],
            ...template,
          };
        }
      })
      .addCase(sendBroadcast.rejected, (state, action) => {
        state.sendStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "sendBroadcast failed";
      })

      .addCase(deleteBroadcastTemplate.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteBroadcastTemplate.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";

        state.templates = state.templates.filter(
          (item) => String(item?.id) !== String(action.payload)
        );
      })
      .addCase(deleteBroadcastTemplate.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "deleteBroadcastTemplate failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  resetSubmitStatus,
  resetSendStatus,
  resetDeleteStatus,
  clearBroadcastTemplatesError,
  clearBroadcastTemplates,
} = broadcastTemplatesSlice.actions;

export default broadcastTemplatesSlice.reducer;